import React, { useCallback, useContext, useEffect } from 'react';
import {
	Button,
	Dropdown,
	Form,
	Segment,
	Tab
} from 'semantic-ui-react';
import { OperationsContext } from '../OperationsContext';
import { useForm } from '../../../Hooks/useForm';
import PlanSummaryTab from './PlanSummaryTab';
import ClaimSummaryTab from './ClaimSummaryTab';
import InsuranceSummaryTab from './InsuranceSummaryTab';
import HighClaimantDetailTab from './HighClaimantDetailTab';

const ClientAnalysisTab = () => {
	// context
	const {
		renewalClientOptions,
		renewalClient,
		renewalClientIndex,
		medicalTiers,
		risks,
		getRenewalClient,
		getNextRenewalClient,
		getPrevRenewalClient,
		updateRenewalClient
	} = useContext( OperationsContext );
	// get the client

	const {
		formData,
		updateField,
		setData
	} = useForm( {} );

	const setDraft = useCallback( () => {
		updateField( { target: { name: 'status', value: 202 } } );
	}, [ updateField ] );

	const setFinal = useCallback( () => {
		updateField( { target: { name: 'status', value: 203 } } );
	}, [ updateField ] );

	useEffect( () => {
		if ( renewalClient ) {
			setData( {
				risk: renewalClient?.risk.id,
				selectedTier: renewalClient?.proposedTier.id,
				status: renewalClient?.reviewStatus.id,
				note: renewalClient?.notes?.[ 0 ]?.note ?? ''
			} );
		}
	}, [
		renewalClient,
		setData
	] );

	const handleSave = useCallback( () => {
		const { notes } = renewalClient;

		notes[ 0 ] = {
			...notes?.[ 0 ] ?? [],
			note: formData.note
		};

		try {
			updateRenewalClient( {
				...renewalClient,
				risk: {
					...renewalClient.risk,
					id: formData.risk
				},
				proposedTier: {
					...renewalClient.proposedTier,
					id: formData.selectedTier
				},
				reviewStatus: {
					...renewalClient.reviewStatus,
					id: formData.status
				},
				notes
			} );
		} catch ( error ) {
			console.error( error );
		}
	}, [
		formData,
		renewalClient,
		updateRenewalClient
	] );

	const panes = [
		{
			menuItem: 'Plan Summary',
			render: () => <PlanSummaryTab />
		},
		{
			menuItem: 'Claim Summary',
			render: () => <ClaimSummaryTab />
		},
		{
			menuItem: 'Insurance Summary',
			render: () => <InsuranceSummaryTab />
		},
		{
			menuItem: 'High Claimant Detail',
			render: () => <HighClaimantDetailTab />
		}
	];

	const selectNewClient = useCallback( ( e, { value } ) => {
		getRenewalClient( value );
	}, [ getRenewalClient ] );

	const handleSelectChange = useCallback( ( e, { name, value } ) => {
		updateField( { target: { name, value } } );
	}, [ updateField ] );

	if ( !renewalClient || !Object.values( formData ).length ) {
		return null;
	}

	return (
		<>
			<Segment>
				{ /* header */}
				<Segment style={{ display: 'flex', alignItems: 'center' }}>
					<div>
						<span style={{
							fontSize: '1.1rem',
							fontWeight: 'bold'
						}}>
							{ renewalClient?.name }
						</span>
						<br />
						<span style={{ color: '#888)' }}>{ renewalClient?.description }</span>
					</div>
					<div style={{
						display: 'flex',
						alignItems: 'center',
						marginLeft: 'auto'
					}}>
						{/* currentTier */}
						<div style={{
							alignItems: 'center',
							display: 'flex',
							flexDirection: 'column',
							marginLeft: '0.5rem'
						}}>
							<span>{ renewalClient?.currentTier?.description }</span>
							<small style={{ color: '#888' }}>CURRENT</small>
						</div>

						{/* suggestedTier */}
						<div style={{
							alignItems: 'center',
							display: 'flex',
							flexDirection: 'column',
							marginLeft: '0.5rem'
						}}>
							<span>{ renewalClient?.suggestedTier?.description }</span>
							<small style={{ color: '#888' }}>SUGGESTED</small>
						</div>

						{/* proposed Tier (selected) */}
						<div style={{
							alignItems: 'center',
							display: 'flex',
							flexDirection: 'column',
							marginLeft: '0.5rem'
						}}>
							<span>{ renewalClient?.proposedTier?.description }</span>
							<small style={{ color: '#888' }}>PROPOSED</small>
						</div>

						{/* risk - lookup type 42 */}
						<div style={{
							alignItems: 'center',
							display: 'flex',
							flexDirection: 'column',
							marginLeft: '0.5rem'
						}}>
							<span>{ renewalClient?.risk?.description }</span>
							<small style={{ color: '#888' }}>RISK</small>
						</div>

						{/* client navigation */}
						<div>
							<Button
								icon="triangle left"
								onClick={ getPrevRenewalClient }
								style={{ marginLeft: '1rem' }}
							/>
							{/* dropdown/search */}
							<Dropdown
								options={ renewalClientOptions }
								search
								selection
								value={ renewalClient.clientID }
								onChange={ selectNewClient }
								style={ { marginLeft: '1rem' } }
							/>
							<Button
								icon="triangle right"
								onClick={ getNextRenewalClient }
								style={{ marginLeft: '1rem' }}
							/>
							{/* client number out of */}
							<span
								style={ {
									color: '#888',
									marginLeft: '1rem'
								} }
							>
								{ renewalClientIndex + 1 } / { renewalClientOptions.length }
							</span>
							{/* arrows */}
						</div>
					</div>
				</Segment>

				{/* filter */}
				<Segment style={{
					display: 'flex',
					alignItems: 'flex-end'
				}}>
					<Form>
						<Form.Group>
							<Form.Input
								label="Suggested Tier"
								value={ renewalClient?.suggestedTier.description ?? '' }
								readOnly
							/>
							<Form.Select
								name="selectedTier"
								label="Selected Tier"
								value={ formData.selectedTier }
								options={ medicalTiers }
								onChange={ handleSelectChange }
							/>
							<Form.Field>
								<label>Status</label>
								<Button.Group>
									<Button
										basic={ formData.status === 203 }
										primary={ formData.status !== 203 }
										onClick={ setDraft }
										style={{ boxShadow: '0 0 0 1px rgba(34, 36, 38, .15) inset' }}
									>
										Draft
									</Button>
									<Button
										basic={ formData.status !== 203 }
										primary={ formData.status === 203 }
										onClick={ setFinal }
										style={{ boxShadow: '0 0 0 1px rgba(34, 36, 38, .15) inset' }}
									>
										Final
									</Button>
								</Button.Group>
							</Form.Field>
							<Form.Select
								name="risk"
								label="Risk"
								value={formData.risk}
								options={risks}
								onChange={handleSelectChange}
								style={{ minWidth: 180 }}
							/>
						</Form.Group>
					</Form>
					<Button
						onClick={ handleSave }
						primary
						style={{
							marginBottom: '1em',
							marginLeft: 'auto'
						}}
					>
						Save
					</Button>
				</Segment>
				<Segment>
					<Form>
						<Form.Group>
							<Form.TextArea
								name="note"
								label="Decision Notes"
								value={ formData.note }
								onChange={ updateField }
								width={ 16 }
								rows={ 4 }
							/>
						</Form.Group>
					</Form>
				</Segment>

				{/* plan table */}
				<Tab
					className="renewal-admin-tabs"
					menu={{
						secondary: true,
						pointing: true
					}}
					panes={panes}
				/>
			</Segment>
		</>
	);
};

export default ClientAnalysisTab;
